/**
 * PSR Phase 2 — Census ACS tract demographics (tier-1 context for sentiment).
 */
import { fetchWithTimeout } from '../dataProviders/types';
import { factToCitationRef } from '../../lib/sentiment/citationRefs';
import { getProviderKey } from '../../lib/providerKeys';
import { hasResolvableSiteGeo, resolveSentimentSiteContext } from './siteContext';
import {
  emptySentimentResult,
  type SentimentFetchOptions,
  type SentimentFetchResult,
  type SentimentFetcher,
} from './types';
import { warnUnkeyedSentimentFetcher } from './unkeyedApiKeyGuard';
import { probeHttpAvailability } from './probeAvailability';
import { rethrowIfAborted, throwIfAborted } from '../../lib/abortSignal';

const CENSUS_ENV_VAR = 'CENSUS_API_KEY'; // R-130-10
// Latest released five-year dataset verified against the Census catalog on
// 2026-09-07. Keep the survey period tied to the version, not retrieval time.
// https://www.census.gov/programs-surveys/acs/data/data-via-api.html
const ACS_YEAR = 2024;
const ACS_PERIOD = `${ACS_YEAR - 4}-${ACS_YEAR} ACS five-year estimate`;

function censusApiKey(): string | undefined {
  return getProviderKey('CENSUS');
}

async function readCensusRows(response: Response): Promise<{ rows?: unknown; error?: string; credentialError?: boolean }> {
  // Census now redirects missing/invalid data-query keys to an HTTP 200 HTML
  // page. Do not print that page or its reflected query, which can contain keys.
  if (response.url && new URL(response.url).pathname.endsWith('/invalid_key.html')) {
    return { error: 'census-acs: Census rejected the configured CENSUS_API_KEY; verify its activation.', credentialError: true };
  }
  try { return { rows: await response.json() }; }
  catch { return { error: 'census-acs: Census returned a non-JSON response; data were not collected. Retry the provider and verify its configured API key.' }; }
}

async function fetchAcsTractFacts(
  fipsState: string,
  fipsCounty: string,
  fipsTract: string,
  signal?: AbortSignal,
): Promise<{ facts: SentimentFetchResult['normalizedFacts']; errors: string[] }> {
  const key = censusApiKey();
  if (!key) {
    warnUnkeyedSentimentFetcher('census');
    return { facts: [], errors: ['census-acs: CENSUS_API_KEY is not configured for this collection run; Census requires a valid key for data queries.'] };
  }
  const vars = 'NAME,B01003_001E,B19013_001E,B25003_002E';
  const geo = `for=tract:${fipsTract}&in=state:${fipsState}%20county:${fipsCounty}`;
  const publicUrl = `https://api.census.gov/data/${ACS_YEAR}/acs/acs5?get=${vars}&${geo}`;
  const url = key ? `${publicUrl}&key=${encodeURIComponent(key)}` : publicUrl;

  const res = await fetchWithTimeout(url, 15_000, signal);
  if (!res.ok) {
    return { facts: [], errors: [`census-acs: HTTP ${res.status}`] };
  }
  const parsed = await readCensusRows(res);
  if (parsed.error) return { facts: [], errors: [parsed.error] };
  const rows = parsed.rows as unknown[][];
  if (!Array.isArray(rows) || rows.length !== 2 || !Array.isArray(rows[0]) || !Array.isArray(rows[1])) {
    return { facts: [], errors: ['census-acs: empty ACS response'] };
  }
  const header = rows[0] as string[];
  const data = rows[1] as (string | number)[];
  const idx = (name: string) => header.indexOf(name);
  if (String(data[idx('state')]) !== fipsState || String(data[idx('county')]) !== fipsCounty || String(data[idx('tract')]) !== fipsTract) {
    return { facts: [], errors: ['census-acs: returned geography does not match requested tract'] };
  }
  const tractName = String(data[idx('NAME')] ?? 'Census tract');
  const numericEstimate = (name: string): number => {
    const raw = data[idx(name)];
    // Census null/suppression codes are missing evidence, never zero.
    if (raw == null || String(raw).trim() === '') return NaN;
    const value = Number(raw);
    return Number.isFinite(value) && value >= 0 ? value : NaN;
  };
  const pop = numericEstimate('B01003_001E');
  const income = numericEstimate('B19013_001E');
  const ownerOcc = numericEstimate('B25003_002E');
  const facts: SentimentFetchResult['normalizedFacts'] = [];
  // Period-end anchor; the label gives the full survey period. This is not a
  // publication date or an assertion that the estimate describes today.
  const observedAt = `${ACS_YEAR}-12-31`;

  if (Number.isFinite(pop)) {
    facts.push({
      key: 'tract_population',
      label: `${tractName} population (${ACS_PERIOD})`,
      value: pop,
      units: 'persons',
      url: publicUrl,
      observedAt,
    });
  }
  if (Number.isFinite(income) && income > 0) {
    facts.push({
      key: 'tract_median_income',
      label: `${tractName} median household income (${ACS_PERIOD}, ${ACS_YEAR} inflation-adjusted dollars)`,
      value: income,
      units: 'USD',
      url: publicUrl,
      observedAt,
    });
  }
  if (Number.isFinite(ownerOcc) && ownerOcc >= 0) {
    facts.push({
      key: 'tract_owner_occupied_units',
      label: `${tractName} owner-occupied housing units (${ACS_PERIOD})`,
      value: ownerOcc,
      units: 'units',
      url: publicUrl,
      observedAt,
    });
  }
  return { facts, errors: facts.length === 3 ? [] : ['census-acs: some estimates are unavailable or suppressed'] };
}

export const censusAcsFetcher: SentimentFetcher = {
  sourceId: 'census-acs',

  async probeAvailability() {
    const key = censusApiKey(); // R-130-10
    // Probe an actual small data query; the public metadata catalog cannot
    // establish whether this credential can retrieve ACS observations.
    const probeUrl = `https://api.census.gov/data/${ACS_YEAR}/acs/acs5?get=NAME&for=us:1&key=${encodeURIComponent(key ?? '')}`;
    return probeHttpAvailability({
      url: probeUrl,
      requiredEnvVarName: CENSUS_ENV_VAR,
      hasRequiredEnvVar: !!key,
      unconfiguredStatuses: [401, 403],
      validateResponse: async response => {
        const result = await readCensusRows(response);
        if (result.credentialError) return { state: 'unconfigured', reason: result.error };
        if (result.error || !Array.isArray(result.rows) || result.rows.length < 2) {
          return { state: 'down', reason: result.error ?? 'Census returned no data rows' };
        }
        return null;
      },
    });
  },

  async isAvailable(siteId, opts) {
    const ctx = await resolveSentimentSiteContext(siteId, opts);
    if (!ctx) return false;
    return !!(ctx.fipsState && ctx.fipsCounty && ctx.fipsTract);
  },

  async fetch(siteId, opts?: SentimentFetchOptions): Promise<SentimentFetchResult> {
    throwIfAborted(opts?.signal);
    try {
      if (!(await hasResolvableSiteGeo(siteId, opts))) {
        return emptySentimentResult('census-acs', ['census-acs: no site coordinates']);
      }
      const ctx = await resolveSentimentSiteContext(siteId, opts);
      if (!ctx?.fipsState || !ctx.fipsCounty || !ctx.fipsTract) {
        return emptySentimentResult('census-acs', ['census-acs: FIPS tract unavailable (FCC lookup failed)']);
      }
      const { facts, errors } = await fetchAcsTractFacts(
        ctx.fipsState,
        ctx.fipsCounty,
        ctx.fipsTract,
        opts?.signal,
      );
      throwIfAborted(opts?.signal);
      if (facts.length === 0) {
        return emptySentimentResult('census-acs', errors.length ? errors : ['census-acs: no ACS facts returned']);
      }
      const citationRefs = facts.map((f, i) => factToCitationRef(f, 'census-acs', i + 1));
      return {
        sourceId: 'census-acs',
        coverage: errors.length ? 'partial' : 'full',
        normalizedFacts: facts,
        citationRefs,
        errors,
        fetchedAt: new Date().toISOString(),
      };
    } catch (e) {
      rethrowIfAborted(opts?.signal, e);
      const msg = e instanceof Error ? e.message : String(e);
      return emptySentimentResult('census-acs', [`census-acs: ${msg}`]);
    }
  },
};
